import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import {
  CheckCircle, XCircle, Warning, Scales, ArrowsClockwise, Spinner
} from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_COLORS = {
  member: "bg-blue-50 text-blue-700 border-blue-200",
  product: "bg-purple-50 text-purple-700 border-purple-200",
  medical: "bg-green-50 text-green-700 border-green-200",
  billing: "bg-orange-50 text-orange-700 border-orange-200",
  fraud: "bg-red-50 text-red-700 border-red-200"
};

const STATUS_ICONS = {
  passed: { icon: CheckCircle, color: "text-green-600", bg: "bg-green-50 border-green-200" },
  warning: { icon: Warning, color: "text-yellow-600", bg: "bg-yellow-50 border-yellow-200" },
  failed: { icon: XCircle, color: "text-red-600", bg: "bg-red-50 border-red-200" }
};

export default function RuleEngineTab({ claim, onReprocess }) {
  const [reprocessing, setReprocessing] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState(false);
  const [policyForm, setPolicyForm] = useState({
    sum_insured: claim.policy_info?.sum_insured || 500000,
    copay_percent: claim.policy_info?.copay_percent || 0,
    deductible: claim.policy_info?.deductible || 0,
    plan_type: claim.policy_info?.plan_type || "standard",
    is_network_hospital: claim.policy_info?.is_network_hospital ?? true
  });

  const ruleResults = claim.rule_results || [];
  const isProcessed = claim.status === "processed";

  const handleReprocess = async () => {
    setReprocessing(true);
    try {
      // First update policy if edited
      if (editingPolicy) {
        await axios.put(`${API}/claims/${claim.id}/policy`, policyForm);
        setEditingPolicy(false);
      }
      await axios.post(`${API}/claims/${claim.id}/reprocess-rules`);
      toast.success("Rules reprocessed successfully");
      onReprocess();
    } catch {
      toast.error("Failed to reprocess rules");
    } finally {
      setReprocessing(false);
    }
  };

  if (!isProcessed) {
    return (
      <div className="p-6 text-center text-gray-400" data-testid="rules-empty">
        <Scales size={48} className="mx-auto mb-3 text-gray-300" />
        <p className="text-sm">Process the claim to view rule engine results.</p>
      </div>
    );
  }

  // Group by category
  const grouped = {};
  ruleResults.forEach(r => {
    const cat = r.category || "other";
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(r);
  });

  return (
    <div className="p-6 space-y-6" data-testid="rules-tab">
      {/* Policy Configuration */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-semibold text-gray-900">Policy Configuration</h3>
          <div className="flex items-center gap-2">
            {!editingPolicy ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setEditingPolicy(true)}
                data-testid="edit-policy-btn"
              >
                Edit Policy
              </Button>
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setEditingPolicy(false)}
              >
                Cancel
              </Button>
            )}
            <Button
              size="sm"
              onClick={handleReprocess}
              disabled={reprocessing}
              className="bg-[#002FA7] hover:bg-blue-800 text-white gap-1.5"
              data-testid="reprocess-btn"
            >
              {reprocessing ? <Spinner size={14} className="animate-spin" /> : <ArrowsClockwise size={14} />}
              Reprocess Rules
            </Button>
          </div>
        </div>

        {editingPolicy && (
          <div className="border border-gray-200 rounded-md p-4 grid grid-cols-2 gap-4 bg-gray-50 mb-4">
            <div>
              <Label className="text-xs">Sum Insured (INR)</Label>
              <Input
                type="number"
                value={policyForm.sum_insured}
                onChange={e => setPolicyForm(p => ({ ...p, sum_insured: Number(e.target.value) }))}
                data-testid="policy-sum-insured"
              />
            </div>
            <div>
              <Label className="text-xs">Copay (%)</Label>
              <Input
                type="number"
                value={policyForm.copay_percent}
                onChange={e => setPolicyForm(p => ({ ...p, copay_percent: Number(e.target.value) }))}
                data-testid="policy-copay"
              />
            </div>
            <div>
              <Label className="text-xs">Deductible (INR)</Label>
              <Input
                type="number"
                value={policyForm.deductible}
                onChange={e => setPolicyForm(p => ({ ...p, deductible: Number(e.target.value) }))}
                data-testid="policy-deductible"
              />
            </div>
            <div>
              <Label className="text-xs">Plan Type</Label>
              <Select
                value={policyForm.plan_type}
                onValueChange={v => setPolicyForm(p => ({ ...p, plan_type: v }))}
              >
                <SelectTrigger data-testid="policy-plan-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="basic">Basic</SelectItem>
                  <SelectItem value="standard">Standard</SelectItem>
                  <SelectItem value="premium">Premium</SelectItem>
                  <SelectItem value="platinum">Platinum</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        )}
      </section>

      {/* Rule Results */}
      {Object.entries(grouped).map(([category, rules]) => (
        <section key={category}>
          <h4 className="text-sm font-semibold text-gray-900 mb-2 capitalize flex items-center gap-2">
            <Badge variant="outline" className={`text-xs ${CATEGORY_COLORS[category] || ""}`}>
              {category}
            </Badge>
            Rules ({rules.length})
          </h4>
          <div className="space-y-2">
            {rules.map((rule, idx) => {
              const statusConfig = STATUS_ICONS[rule.status] || STATUS_ICONS.warning;
              const Icon = statusConfig.icon;
              return (
                <div
                  key={idx}
                  className={`border rounded-md p-3 flex items-start gap-3 ${statusConfig.bg}`}
                  data-testid={`rule-${category}-${idx}`}
                >
                  <Icon size={18} weight="fill" className={`${statusConfig.color} mt-0.5 flex-shrink-0`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900">{rule.rule_name}</p>
                    <p className="text-xs text-gray-600 mt-0.5">{rule.message}</p>
                  </div>
                  <Badge
                    variant="outline"
                    className={`text-xs flex-shrink-0 ${
                      rule.status === "passed" ? "bg-green-100 text-green-800 border-green-300" :
                      rule.status === "failed" ? "bg-red-100 text-red-800 border-red-300" :
                      "bg-yellow-100 text-yellow-800 border-yellow-300"
                    }`}
                  >
                    {rule.status}
                  </Badge>
                </div>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
}
